import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import eventService from "services/eventService";
import Container from "@mui/material/Container";
import Card from "@mui/material/Card";
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import Navbar from "sections/Navbars";
import Footer from "sections/Footers";
import Header from "sections/Headers";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import frLocale from "@fullcalendar/core/locales/fr";
import bgImage from "assets/imgs/headers/bg-calendar.jpg";
import routes from "routes";
import footerRoutes from "footer.routes";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import { useAuth } from "auth/AuthProvider";
import { canEdit } from "auth/roles";
import CreateEventDialog from "components/CreateEventDialog";

function Calendar() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [openCreate, setOpenCreate] = useState(false);
  const [initials, setInitials] = useState(null); // { startDate, startTime, endDate, endTime, allDay }
  const navigate = useNavigate();

  const fetchEvents = async (start, end) => {
    try {
      const eventsData = await eventService.getEventsInRange(start, end);
      const transformedEvents = eventsData.map((event) => ({
        title: event.title,
        start: event.startDate,
        end: event.endDate,
        allDay: event.allDay,
        extendedProps: {
          slug: event.slug,
          description: event.description,
        },
      }));
      setEvents(transformedEvents);
    } catch (error) {
      console.error("Erreur lors de la récupération des événements :", error);
    }
  };

  const handleDatesSet = (dateInfo) => {
    const start = new Date(dateInfo.start).toISOString();
    const end = new Date(dateInfo.end).toISOString();
    if (start !== dateRange.start || end !== dateRange.end) {
      setDateRange({ start, end });
    }
  };

  useEffect(() => {
    if (dateRange.start && dateRange.end) {
      fetchEvents(dateRange.start, dateRange.end);
    }
  }, [dateRange]);

  const breadcrumbs = [
    { label: "Accueil", route: "/" },
    { label: "Calendrier", route: "/calendrier" },
  ];

  // Helpers préremplissage
  function toParts(dt) {
    // dt: Date
    const pad = (n) => `${n}`.padStart(2, "0");
    return {
      date: `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`,
      time: `${pad(dt.getHours())}:${pad(dt.getMinutes())}`,
    };
  }

  return (
    <>
      <Navbar routes={routes} sticky />
      <Header bgImage={bgImage} title="Calendrier" breadcrumbs={breadcrumbs} />
      <Container sx={{ mt: -8, mb: 4 }}>
        <Card sx={{ p: 2, mx: { xs: 2, lg: 3 }, boxShadow: ({ boxShadows: { xxl } }) => xxl }}>
          <MKBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <MKTypography variant="h6">Calendrier</MKTypography>
            {canEdit(user) && (
              <Tooltip title="Créer un évènement">
                <IconButton
                  onClick={() => {
                    // ouverture sans date sélectionnée
                    setInitials(null);
                    setOpenCreate(true);
                  }}
                >
                  <AddCircleOutline />
                </IconButton>
              </Tooltip>
            )}
          </MKBox>

          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, listPlugin]}
            initialView={window.innerWidth < 768 ? "listMonth" : "dayGridMonth"}
            events={events}
            locales={[frLocale]}
            locale="fr"
            headerToolbar={{
              left: window.innerWidth < 768 ? "prev,next" : "prev,next today",
              center: "title",
              right:
                window.innerWidth < 768 ? "" : "dayGridMonth,timeGridWeek,timeGridDay,listMonth",
            }}
            height="auto"
            datesSet={handleDatesSet}
            selectable={canEdit(user)}
            selectMirror
            select={(info) => {
              // info.start / info.end sont des Date en local
              const s = toParts(info.start);
              // Pour FullCalendar, end est exclusif => retire 1 minute pour une meilleure suggestion
              const eDate = new Date(info.end);
              eDate.setMinutes(eDate.getMinutes() - 1);
              const e = toParts(eDate);

              const isAllDay = info.allDay || (s.time === "00:00" && e.time === "23:59");
              setInitials({
                startDate: s.date,
                startTime: isAllDay ? "" : s.time,
                endDate: e.date,
                endTime: isAllDay ? "" : e.time,
                allDay: isAllDay,
              });
              setOpenCreate(true);
            }}
            eventContent={(eventInfo) => (
              <div
                style={{ overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}
                className="calendarEvent"
              >
                <b>{eventInfo.timeText}</b> <i>{eventInfo.event.title}</i>
              </div>
            )}
            eventClick={(info) => {
              navigate(`/calendrier/${info.event.extendedProps.slug}`);
            }}
          />
        </Card>
      </Container>
      <MKBox pt={6} px={1} mt={6}>
        <Footer content={footerRoutes} />
      </MKBox>

      {/* Modale création, avec préremplissage si sélection */}
      {canEdit(user) && (
        <CreateEventDialog
          open={openCreate}
          onClose={() => setOpenCreate(false)}
          onCreated={() => {
            setOpenCreate(false);
            // recharger la plage visible
            if (dateRange.start && dateRange.end) fetchEvents(dateRange.start, dateRange.end);
          }}
          initialStartDate={initials?.startDate}
          initialStartTime={initials?.startTime}
          initialEndDate={initials?.endDate}
          initialEndTime={initials?.endTime}
          initialAllDay={initials?.allDay}
        />
      )}
    </>
  );
}

export default Calendar;
